import React from 'react';
import { getLocationName } from './locationUtils';
import { formatTimeRange } from './helpers';

const Step5Review = React.memo(function Step5Review({
  userType,
  newPatientData,
  existingPatientResult,
  savedPatientId,
  providers,
  selectedProvider,
  selectedLocation,
  selectedVisitDate,
  selectedTimeSlot,
  selectedNextAvailableSlot,
  bookingData,
  visitTypes,
  selectedVisitType,
  insuranceNumber,
  preferredLanguage,
  debugMode,
  lastApiResponse,
  apiError,
  handleFinalSubmit,
  isSubmitting,
}) {
  const selectedProviderData = providers.find(p => p.ResourceId.toString() === selectedProvider);

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Review Your Appointment</h2>
        <p className="text-gray-600">Please review all information before submitting</p>
      </div>

      {/* Patient Information */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Patient Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          {userType === 'new' ? (
            <>
              <div><strong>Name:</strong> {newPatientData.first_name} {newPatientData.last_name}</div>
              <div><strong>Date of Birth:</strong> {newPatientData.dob}</div>
              <div><strong>Sex:</strong> {newPatientData.sex === '1' ? 'Female' : newPatientData.sex === '2' ? 'Male' : 'Not selected'}</div>
              <div><strong>Email:</strong> {newPatientData.email}</div>
              <div><strong>Mobile Phone:</strong> {newPatientData.mobile_phone}</div>
              <div><strong>Address:</strong> {newPatientData.address}</div>
              <div><strong>ZIP Code:</strong> {newPatientData.zipcode}</div>
              <div><strong>Patient ID:</strong> {savedPatientId}</div>
              {insuranceNumber && (
                <div><strong>Insurance Number:</strong> {insuranceNumber}</div>
              )}
              {preferredLanguage && (
                <div><strong>Preferred Language:</strong> {preferredLanguage}</div>
              )}
            </>
          ) : (
            <>
              <div><strong>Name:</strong> {existingPatientResult?.FirstName} {existingPatientResult?.LastName}</div>
              <div><strong>MRN:</strong> {existingPatientResult?.MRNNumber}</div>
              <div><strong>Address:</strong> {existingPatientResult?.Address}</div>
              <div><strong>Patient ID:</strong> {existingPatientResult?.PatientId}</div>
            </>
          )}
        </div>
      </div>

      {/* Appointment Details */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-4">Appointment Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div><strong>Location:</strong> {getLocationName(selectedLocation)}</div>
          <div><strong>Provider:</strong> {selectedProviderData?.ProviderName}</div>
          <div>
            <strong>Date:</strong>{' '}
            {selectedNextAvailableSlot?.dateDisplay || (() => {
              const [year, month, day] = selectedVisitDate.split('-');
              const dateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
              return dateObj.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
            })()}
          </div>
          <div>
            <strong>Time:</strong>{' '}
            {selectedTimeSlot && formatTimeRange(selectedTimeSlot, bookingData.duration).displayRange}
          </div>
          <div><strong>Duration:</strong> {bookingData.duration} minutes</div>
          <div>
            <strong>Visit Type:</strong>{' '}
            {visitTypes.find(vt => vt.id.toString() === selectedVisitType)?.name || 'Not selected'}
          </div>
          {debugMode && (
            <div className="md:col-span-2 mt-2 p-2 bg-yellow-100 border border-yellow-300 rounded text-xs">
              <strong>API Date Format:</strong> {bookingData.preferred_date} (should be MM/DD/YYYY)
            </div>
          )}
        </div>
      </div>

      {/* Debug Info */}
      {debugMode && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg">
          <details className="group">
            <summary className="cursor-pointer p-6 hover:bg-yellow-100 transition-colors">
              <h3 className="text-lg font-semibold text-yellow-900 inline-flex items-center">
                🔍 Debug: Booking Parameters
                <svg className="w-5 h-5 ml-2 transform group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </h3>
            </summary>
            <div className="px-6 pb-6">
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-yellow-800 mb-2">Final Booking Data to be sent to /booking/book:</h4>
                  <pre className="text-xs text-yellow-700 bg-white p-3 rounded border overflow-x-auto">
                    {(() => {
                      const patientComments = userType === 'new' ? newPatientData.comments : '';
                      return JSON.stringify({
                        ...bookingData,
                        comments: patientComments
                          ? `Online booking - ${userType} patient\n\nPatient Comments:\n${patientComments}`
                          : `Online booking - ${userType} patient`,
                        resource_type: bookingData.resource_type || null,
                        flexible_date_from: bookingData.flexible_date_from || null,
                        flexible_date_to: bookingData.flexible_date_to || null,
                        flexible_time_from: bookingData.flexible_time_from || null,
                        flexible_time_to: bookingData.flexible_time_to || null
                      }, null, 2);
                    })()}
                  </pre>
                </div>
                <div>
                  <h4 className="font-semibold text-yellow-800 mb-2">Patient Data Used (Processed for API):</h4>
                  <pre className="text-xs text-yellow-700 bg-white p-3 rounded border overflow-x-auto">
                    {userType === 'new'
                      ? JSON.stringify({
                          ...newPatientData,
                          sex: parseInt(newPatientData.sex) || 2,
                          mobile_phone: (newPatientData.mobile_phone || '').replace(/[^\d]/g, ''),
                          home_phone: '',
                          zip_id: newPatientData.zip_id,
                          patient_id_encrypted: ''
                        }, null, 2)
                      : JSON.stringify({
                          PatientId: existingPatientResult?.PatientId,
                          FirstName: existingPatientResult?.FirstName,
                          LastName: existingPatientResult?.LastName,
                          DOB: existingPatientResult?.DOB,
                          MRNNumber: existingPatientResult?.MRNNumber,
                          Address: existingPatientResult?.Address,
                          EncryptPatientId: existingPatientResult?.EncryptPatientId,
                          is_new_api_patient: false
                        }, null, 2)
                    }
                  </pre>
                </div>
              </div>
            </div>
          </details>
        </div>
      )}

      {/* Error Display */}
      {apiError && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
          <h4 className="font-semibold mb-1">❌ Error:</h4>
          <p className="text-sm">{apiError}</p>
        </div>
      )}

      <div className="flex justify-end pt-4">
        <button
          type="button"
          onClick={handleFinalSubmit}
          disabled={isSubmitting}
          className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
        >
          {isSubmitting ? 'Booking Appointment...' : 'Book Appointment'}
        </button>
      </div>
    </div>
  );
});

export default Step5Review;
